import type { Locale } from '@xfile/shared';
import type { Messages } from './messages.fr';

/**
 * CHARGEMENT À LA DEMANDE DES CATALOGUES (dette n° 3 résorbée).
 *
 * Les trois catalogues partaient dans le bundle initial alors qu'UN SEUL sert : mesuré sur un
 * build de production, DE + EN pesaient **13,24 kio gzippés** sur un chargement initial de
 * 149,23 kio, soit près de 9 % de JavaScript téléchargé pour rien.
 *
 * On charge donc la seule langue active, en `import()` dynamique — Vite en fait un chunk par
 * langue. `t()` RESTE SYNCHRONE : le catalogue est résolu AVANT le premier rendu
 * (`main.tsx`), puis lu ici de façon synchrone (`peekCatalog`). Le typage strict est
 * intégralement préservé : `Messages` et `MessageKey` viennent d'un import de TYPE
 * (`messages.fr`), effacé à la compilation, qui ne force donc aucun chargement à l'exécution.
 *
 * ⚠ Ces `import()` doivent rester des littéraux statiquement analysables (pas de chemin
 * calculé) : c'est ce qui permet à Rollup de produire les trois chunks et au plugin
 * `vite-plugin-locale-preload` d'en retrouver les noms hashés.
 */
type CatalogLoader = () => Promise<Messages>;

const LOADERS: Record<Locale, CatalogLoader> = {
  fr: () => import('./messages.fr').then((m) => m.fr),
  de: () => import('./messages.de').then((m) => m.de),
  en: () => import('./messages.en').then((m) => m.en),
};

/** Catalogues déjà résolus (une langue chargée le reste pour la session). */
const loaded = new Map<Locale, Messages>();
/** Chargements en cours, dédoublonnés (deux bascules rapides ⇒ une seule requête). */
const inFlight = new Map<Locale, Promise<Messages>>();

/**
 * Catalogue DÉJÀ chargé, ou `null`. Lecture SYNCHRONE — c'est elle qui permet à `t()` de le
 * rester une fois la langue hydratée.
 */
export function peekCatalog(locale: Locale): Messages | null {
  return loaded.get(locale) ?? null;
}

/**
 * Enregistre un catalogue déjà en main, sans passer par le réseau.
 *
 * Utilisé par le setup des tests (`test-setup.ts`), qui importe STATIQUEMENT les trois
 * catalogues : les centaines de tests qui rendent `<I18nProvider>` et lisent aussitôt du
 * texte français restent ainsi rigoureusement synchrones, sans `await` ajouté nulle part.
 */
export function registerCatalog(locale: Locale, messages: Messages): void {
  loaded.set(locale, messages);
}

/** Charge (ou retrouve) le catalogue d'une langue. Idempotent et dédoublonné. */
export async function loadCatalog(locale: Locale): Promise<Messages> {
  const cached = loaded.get(locale);
  if (cached !== undefined) return cached;

  const pending = inFlight.get(locale);
  if (pending !== undefined) return pending;

  const request = LOADERS[locale]()
    .then((messages) => {
      loaded.set(locale, messages);
      return messages;
    })
    .finally(() => {
      // Un échec (réseau coupé pendant la bascule de langue) ne doit pas rester en cache :
      // sans cela, toute nouvelle tentative rejouerait le MÊME rejet, pour toujours.
      inFlight.delete(locale);
    });

  inFlight.set(locale, request);
  return request;
}
